import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ServiceQuoteModal } from "@/components/ServiceQuoteModal";
import { Box, Construction, HardHat, MapPin, Shield, FileCheck, CheckCircle, Truck } from "lucide-react";
import { NavLink } from "@/components/NavLink";
import { useState } from "react";
import projectCargoImg from "@/assets/project-cargo.jpg";
import bannerTerminal from "@/assets/banner-terminal.jpg";

const ProjectCargo = () => {
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);

  const capabilities = [
    {
      icon: Construction,
      title: "Heavy Lift",
      description: "Specialized equipment for cargo exceeding standard weight limits",
      features: ["Up to 1,000+ tons", "Hydraulic cranes", "Strand jacks", "Specialized vessels"]
    },
    {
      icon: Box,
      title: "Out-of-Gauge Cargo",
      description: "Handling oversized items that exceed standard container dimensions",
      features: ["Open-top containers", "Flat racks", "Platform containers", "Breakbulk solutions"]
    },
    {
      icon: HardHat,
      title: "Engineering Solutions",
      description: "Custom engineering for complex logistics challenges",
      features: ["Route surveys", "Structural analysis", "Lifting plans", "Risk assessments"]
    },
    {
      icon: MapPin,
      title: "Site Delivery",
      description: "Complete logistics from port to final installation site",
      features: ["Escort services", "Permit management", "Civil works coordination", "On-site handling"]
    }
  ];

  const industries = [
    { name: "Energy & Power", projects: "Wind turbines, transformers, generators, solar equipment" },
    { name: "Oil & Gas", projects: "Refinery modules, pressure vessels, pipeline equipment" },
    { name: "Construction", projects: "Cranes, excavators, steel structures, precast elements" },
    { name: "Mining", projects: "Haul trucks, crushers, conveyor systems, processing plants" },
    { name: "Marine", projects: "Ship components, offshore platforms, subsea equipment" },
    { name: "Infrastructure", projects: "Bridge sections, tunnel boring machines, rail equipment" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar onQuoteClick={() => setQuoteModalOpen(true)} />
      
      {/* Hero Section */}
      <section className="relative h-[500px] flex items-center justify-center overflow-hidden">
        <img 
          src={projectCargoImg} 
          alt="Project Cargo" 
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-maritime-dark/90 to-maritime-dark/70" />
        <div className="relative z-10 text-center text-primary-foreground px-4 animate-fade-in">
          <Box className="h-16 w-16 mx-auto mb-6" />
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Project & Heavy Cargo</h1>
          <p className="text-xl max-w-3xl mx-auto mb-8 opacity-90">
            Specialized logistics for oversized, overweight, and high-value cargo. Expert handling from planning to delivery.
          </p>
          <Button size="lg" onClick={() => setQuoteModalOpen(true)} className="hover-scale">
            Discuss Your Project
          </Button>
        </div>
      </section>

      <main className="py-20">
        <div className="container mx-auto px-4">
          {/* Overview */}
          <section className="mb-20 animate-fade-in">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold mb-6">Expertise in Complex Logistics</h2>
                <p className="text-lg text-muted-foreground mb-6">
                  When standard shipping won't work, our project cargo team steps in. With decades of experience handling wind turbines, heavy machinery, and industrial equipment, we deliver solutions where others see obstacles.
                </p>
                <p className="text-lg text-muted-foreground mb-6">
                  From our California headquarters, we coordinate complex multimodal movements across the globe, ensuring your valuable cargo arrives safely and on schedule.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-secondary rounded-lg">
                    <div className="text-3xl font-bold text-primary">1,000+</div>
                    <div className="text-sm text-muted-foreground">Tons Max Lift</div>
                  </div>
                  <div className="text-center p-4 bg-secondary rounded-lg">
                    <div className="text-3xl font-bold text-primary">50+</div>
                    <div className="text-sm text-muted-foreground">Countries Served</div>
                  </div>
                  <div className="text-center p-4 bg-secondary rounded-lg">
                    <div className="text-3xl font-bold text-primary">100%</div>
                    <div className="text-sm text-muted-foreground">Project Success Rate</div>
                  </div>
                  <div className="text-center p-4 bg-secondary rounded-lg">
                    <div className="text-3xl font-bold text-primary">24/7</div>
                    <div className="text-sm text-muted-foreground">Project Management</div>
                  </div>
                </div>
              </div>
              <div className="relative">
                <img 
                  src={bannerTerminal} 
                  alt="Heavy Cargo Operations" 
                  className="rounded-xl shadow-2xl"
                />
              </div>
            </div>
          </section>

          {/* Capabilities */}
          <section className="mb-20">
            <h2 className="text-4xl font-bold text-center mb-12">Our Capabilities</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {capabilities.map((capability, index) => {
                const Icon = capability.icon;
                return (
                  <Card key={index} className="hover:shadow-xl transition-all hover-scale animate-fade-in">
                    <CardContent className="p-8">
                      <Icon className="h-12 w-12 text-primary mb-4" />
                      <h3 className="text-2xl font-bold mb-3">{capability.title}</h3>
                      <p className="text-muted-foreground mb-6">{capability.description}</p>
                      <div className="space-y-2">
                        {capability.features.map((feature, idx) => (
                          <div key={idx} className="flex items-center text-sm">
                            <CheckCircle className="w-4 h-4 text-primary mr-2" />
                            <span>{feature}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          {/* Process */}
          <section className="bg-secondary rounded-xl p-12 mb-20 animate-fade-in">
            <h2 className="text-3xl font-bold text-center mb-12">Project Cargo Process</h2>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
              {[
                { step: "1", title: "Consultation", desc: "Understanding your requirements" },
                { step: "2", title: "Survey", desc: "Route & site assessments" },
                { step: "3", title: "Planning", desc: "Engineering & logistics design" },
                { step: "4", title: "Execution", desc: "Coordinated transport operations" },
                { step: "5", title: "Monitoring", desc: "Real-time tracking & updates" },
                { step: "6", title: "Delivery", desc: "Safe arrival & handover" }
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto mb-3 text-lg font-bold">
                    {item.step}
                  </div>
                  <h3 className="font-semibold text-sm mb-1">{item.title}</h3>
                  <p className="text-xs text-muted-foreground">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Industries */}
          <section className="mb-20">
            <h2 className="text-4xl font-bold text-center mb-12">Industries We Serve</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {industries.map((industry, index) => (
                <Card key={index} className="hover:shadow-lg transition-all hover-scale animate-fade-in">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold mb-3">{industry.name}</h3>
                    <p className="text-muted-foreground text-sm">{industry.projects}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Equipment */}
          <section className="mb-20">
            <h2 className="text-4xl font-bold text-center mb-12">Specialized Equipment</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[
                { name: "Flat Rack Containers", desc: "For oversized cargo" },
                { name: "Open Top Containers", desc: "Top-loading capability" },
                { name: "Multi-Axle Trailers", desc: "For heavy transport" },
                { name: "Self-Propelled Modules", desc: "For extremely heavy loads" },
                { name: "Mobile Cranes", desc: "On-site lifting" },
                { name: "Ro-Ro Vessels", desc: "Roll-on/roll-off ships" },
                { name: "Heavy Lift Vessels", desc: "Ship-mounted cranes" },
                { name: "Barges", desc: "Inland waterway transport" }
              ].map((equipment, index) => (
                <Card key={index} className="text-center hover-scale animate-fade-in">
                  <CardContent className="p-4">
                    <Truck className="h-8 w-8 text-primary mx-auto mb-2" />
                    <h3 className="font-semibold text-sm">{equipment.name}</h3>
                    <p className="text-xs text-muted-foreground">{equipment.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Why Choose Us */}
          <section className="mb-20">
            <h2 className="text-4xl font-bold text-center mb-12">Why Choose Our Project Cargo Services?</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { icon: Shield, title: "Risk Management", desc: "Comprehensive insurance, safety protocols, and contingency planning for every project" },
                { icon: FileCheck, title: "Documentation", desc: "Expert handling of permits, customs, and regulatory requirements worldwide" },
                { icon: HardHat, title: "Expert Team", desc: "Dedicated project managers with engineering backgrounds and decades of experience" }
              ].map((item, index) => {
                const Icon = item.icon;
                return (
                  <Card key={index} className="text-center hover-scale animate-fade-in">
                    <CardContent className="p-8">
                      <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Icon className="h-8 w-8 text-primary" />
                      </div>
                      <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                      <p className="text-muted-foreground">{item.desc}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          {/* CTA */}
          <section className="text-center bg-primary text-primary-foreground rounded-xl p-12 animate-fade-in">
            <h2 className="text-3xl font-bold mb-4">Have a Complex Cargo Challenge?</h2>
            <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
              Let our project cargo specialists analyze your requirements and develop a tailored solution.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="secondary" onClick={() => setQuoteModalOpen(true)} className="hover-scale">
                Request Project Consultation
              </Button>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-primary" asChild>
                <NavLink to="/contact">Contact Project Team</NavLink>
              </Button>
            </div>
          </section>
        </div>
      </main>

      <Footer />
      <ServiceQuoteModal open={quoteModalOpen} onOpenChange={setQuoteModalOpen} serviceType="project-cargo" />
    </div>
  );
};

export default ProjectCargo;
